import React, { useState } from "react";
import { useParams, useLocation, Link, Navigate } from "react-router-dom";
import { getCityBySlug, CITIES } from "../data/cities";
import PageTopSlider from "../components/PageTopSlider";
import ContactForm from "../components/ContactForm";
import ReviewsSlider from "../components/ReviewsSlider";
import GalleryViewer from "../components/GalleryViewer";
import SEO from "../components/SEO";

export default function CityEnergimaerkning({ cityProp }) {
  const { citySlug } = useParams();
  const location = useLocation();

  const slugFromPath = location.pathname.replace(/^\/energimaerkning-i-/, "");
  const city = cityProp || getCityBySlug(citySlug) || getCityBySlug(slugFromPath);

  const [openFaq, setOpenFaq] = useState(null);

  const faqList = [
    {
      q: "Hvad betyder energimærkning?",
      a: "Energimærkning er en vurdering af en bygnings energiforbrug og effektivitet. Mærket viser, hvor meget energi ejendommen bruger, og hvor der er mulighed for forbedringer. Skalaen går fra A2020, som er det mest energieffektive niveau, til G, som er det mindst effektive.",
    },
    {
      q: "Hvad koster det at få lavet energimærkning hos ET Danmark ApS?",
      a: "Prisen for en energimærkning starter fra 4.188 kr. inkl. moms. Prisen dækker gennemgangen af ejendommen samt udarbejdelsen af den tilhørende rapport med anbefalinger til energioptimering.",
    },
    {
      q: "Hvordan beregnes energimærkning?",
      a: "Energimærkningen beregnes af en certificeret energikonsulent, der foretager en grundig gennemgang af bygningen. Konsulenten vurderer blandt andet isolering, vinduer, døre, ventilation og varmeanlæg. På baggrund af gennemgangen og beregninger af energiforbruget tildeles ejendommen en karakter på skalaen fra A2020 til G.",
    },
    {
      q: "Hvad er bedst energimærke A eller F?",
      a: "Et energimærke A er langt bedre end et energimærke F. A-mærkede bygninger har lavt energiforbrug og høj effektivitet, mens F-mærkede bygninger bruger markant mere energi. Et godt energimærke betyder lavere udgifter, højere komfort og typisk også en højere ejendomsværdi.",
    },
    {
      q: "Hvor længe holder et energimærke?",
      a: "Et energimærke er gyldigt i 10 år fra udstedelsesdatoen. Efter denne periode skal der udarbejdes en ny energimærkning for at sikre, at vurderingen stadig afspejler bygningens aktuelle energistandard og eventuelle forbedringer.",
    },
    {
      q: "Hvem må lave energimærket?",
      a: "Kun certificerede energikonsulenter må udarbejde energimærkninger. Energikonsulenter har den nødvendige uddannelse og autorisation til at vurdere en bygnings energiforbrug og udarbejde den officielle rapport.",
    },
    {
      q: "Hvordan får man et energimærke?",
      a: "For at få et energimærke skal man kontakte en certificeret energikonsulent, som gennemgår ejendommen og beregner energiforbruget. Efter gennemgangen modtager ejeren en rapport med både energimærket og konkrete forslag til forbedringer, der kan øge energieffektiviteten og reducere energiomkostningerne.",
    },
    {
      q: "Hvornår skal man have et energimærke?",
      a: "Energimærkning er lovpligtig ved salg og udlejning af ejendomme. Formålet er at give købere eller lejere et klart billede af bygningens energiforbrug, før de træffer beslutning. Derudover kan det være relevant at få en ny mærkning efter større renoveringer, hvor energieffektiviteten er forbedret.",
    },
    {
      q: "Hvad kigger man på i et energimærke?",
      a: "Når en bygning energimærkes, gennemgår en energikonsulent blandt andet isolering, vinduer, varmeanlæg, ventilation og bygningens generelle tilstand. Disse faktorer danner grundlag for den samlede vurdering af ejendommens energiforbrug og potentiale for optimering.",
    },
    {
      q: "Hvordan forbedrer man sit energimærke?",
      a: "Der findes flere måder at forbedre et energimærke på. Typiske tiltag inkluderer bedre isolering, udskiftning af vinduer og døre, installation af energieffektive varmeanlæg og optimeret ventilation. Disse forbedringer kan reducere varmeforbruget, øge komforten og samtidig gøre ejendommen mere attraktiv ved salg eller udlejning.",
    },
  ];

  if (!city) {
    return <Navigate to="/404" replace />;
  }

  const otherCities = CITIES.filter((c) => c.slug !== city.slug);

  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title={`Energimærkning i ${city.name} | Energioptimering | Få et tilbud`}
        description={`Vi har stor erfaring med energimærkning og energioptimering i ${city.name}. Få hjælp til at identificere de bedste muligheder for dit hus.`}
        canonical={`https://www.etdanmark.com/energimaerkning-i-${city.slug}`}
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07577.jpg"
        faqs={faqList}
      />
      {/* 1. TOP HERO */}
      <PageTopSlider
        title={`Energimærkning i ${city.name}`}
        subtitle={`Vi har stor erfaring med energimærkning og energioptimering i ${city.name}. Få hjælp til at identificere de bedste muligheder for dit hus.`}
        badge={`Certificeret energikonsulent i ${city.name}`}
        priceBadge="Fra 4.188 kr. inkl. moms"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Energimærkning", to: "/energimaerkning" },
          { label: `Energimærkning i ${city.name}` },
        ]}
        actions={
          <>
            <a
              href="tel:+4542499595"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
              <span>Ring: 42 49 95 95</span>
            </a>
            <Link
              to="/prisberegner"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-calculator text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Beregn din pris</span>
            </Link>
          </>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07577.jpg",
          alt: `Energimærkning i ${city.name}`,
        }}
      />

      {/* 2. INTRODUKTION & PRIS */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8 space-y-5">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-stone-900 tracking-tight">
                Professionel energimærkning i {city.name}
              </h2>
              <p className="text-stone-700 text-base leading-relaxed">
                Er du klar over, hvordan en god energimærkning kan øge værdien af din ejendom, og samtidig reducere dine energiomkostninger?
              </p>
              <p className="text-stone-700 text-base leading-relaxed">
                Mange husejere og bygningsansvarlige er stadig i tvivl om, hvordan energimærkning af bygninger fungerer, og hvilken betydning det har for deres økonomi og miljø.
              </p>
              <p className="text-stone-700 text-base leading-relaxed">
                Har du behov for rådgivning omkring hvordan du bedst kan optimere din bolig, kan vi også være behjælpelige. Vi har hjulpet mange kunder med energimærkning i {city.name} og står også klar til at hjælpe dig.
              </p>

              <div className="pt-2 flex flex-wrap gap-4 items-center">
                <Link
                  to="/kontakt"
                  className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all inline-flex items-center gap-2"
                >
                  <span>Kontakt os</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/prisberegner"
                  className="px-6 py-3 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-sm transition-all"
                >
                  Gå til prisberegner
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4">
              <div className="p-8 rounded-3xl bg-gradient-to-br from-emerald-900 to-emerald-950 text-white shadow-xl border border-emerald-800/40 text-center space-y-4">
                <div className="inline-block px-3 py-1 rounded-full bg-emerald-700/60 text-emerald-200 text-xs font-semibold uppercase tracking-wider">
                  Gennemskuelig pris
                </div>
                <div className="text-xs text-emerald-200 uppercase tracking-wider">
                  Vejledende pris
                </div>
                <div className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                  Fra 4.188 <span className="text-xl font-normal text-emerald-300">kr.</span>
                </div>
                <div className="text-xs text-emerald-200 font-medium">
                  inkl. moms & komplet rapport
                </div>
                <p className="text-xs text-emerald-300/90 pt-2 leading-relaxed border-t border-emerald-800/60">
                  Gennemgang af ejendommen samt officiel energimærkerapport med konkrete forslag til optimering.
                </p>
                <a
                  href="tel:+4542499595"
                  className="w-full mt-2 py-2.5 rounded-full bg-white hover:bg-stone-100 text-emerald-950 font-bold text-xs inline-flex items-center justify-center gap-2 transition-colors shadow-sm"
                >
                  <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                  <span>Ring direkte: 42 49 95 95</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. HVAD ER ENERGIMÆRKNING & FORDELE */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-6 space-y-4">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Hvad er energimærkning?
              </h2>
              <p className="text-stone-700 text-sm leading-relaxed">
                Energimærkning er en vurdering af et bygnings energiforbrug. Energimærket giver en indikation af, hvor energieffektiv din bygning er, og hvad der kan forbedres for at reducere energiforbruget. Energimærket spænder fra A2020, som er det bedste, til G, som er det dårligste.
              </p>
              <p className="text-stone-700 text-sm leading-relaxed">
                Dette mærke er lovpligtigt ved salg og udlejning af ejendomme, og det giver potentielle købere eller lejere en idé om bygningens energiforbrug.
              </p>

              <div className="pt-4 space-y-4">
                <h3 className="text-xl font-bold text-stone-900">
                  Hvordan foregår energimærkning?
                </h3>
                <p className="text-stone-700 text-sm leading-relaxed">
                  Processen for at energimærke bygninger, indebærer en grundig gennemgang af ejendommen af en certificeret energikonsulent. Konsulenten vurderer isolering, varmeinstallationer, vinduer, ventilation og andre energirelaterede aspekter af bygningen.
                </p>
                <p className="text-stone-700 text-sm leading-relaxed">
                  Efter denne gennemgang udstedes et energimærke, der placerer bygningen på en skala fra A2020 (meget energieffektiv) til G (mindre energieffektiv). Samtidig modtager du en rapport med specifikke anbefalinger til energioptimering af huset. Disse forslag kan omfatte forbedringer som bedre isolering, udskiftning af vinduer og modernisering af varmeanlæg og ventilationssystemer.
                </p>
                <p className="text-stone-700 text-sm leading-relaxed">
                  Rapporten fungerer som en vejledning til at reducere energiomkostningerne, forbedre komforten og øge ejendommens værdi ved fremtidige salg eller udlejning.
                </p>
              </div>
            </div>

            <div className="lg:col-span-6 space-y-6">
              <div className="p-8 rounded-3xl bg-white border border-stone-200/80 shadow-sm space-y-6">
                <h3 className="text-xl font-bold text-stone-900">
                  Fordele ved energimærkning
                </h3>
                <p className="text-stone-600 text-sm">
                  En god energimærkning af huse, kan føre til flere fordele:
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-chart-line text-sm" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-sm">Øget ejendomsværdi</h4>
                      <p className="text-xs text-stone-600 mt-0.5">
                        Huse med bedre energioptimering sælges ofte hurtigere og til en højere pris.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-wallet text-sm" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-sm">Reducerede energiomkostninger</h4>
                      <p className="text-xs text-stone-600 mt-0.5">
                        Ved at følge anbefalingerne fra energimærkningen, kan du reducere dine energiudgifter betydeligt.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-leaf text-sm" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-sm">Miljømæssige fordele</h4>
                      <p className="text-xs text-stone-600 mt-0.5">
                        Lavere energiforbrug betyder mindre CO2-udledning, hvilket er godt for miljøet.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fa-solid fa-couch text-sm" aria-hidden="true"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-stone-900 text-sm">Komfort</h4>
                      <p className="text-xs text-stone-600 mt-0.5">
                        Bedre isolering og energieffektivitet øger komforten i dit hjem.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. ENERGIOPTIMERING AF HUS I [BY] & NY ENERGIMÆRKNING */}
      <section className="relative overflow-hidden py-16 sm:py-20 border-b border-stone-200/70">
        {/* Baggrundsbillede bag boksene */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg"
            alt={`Energioptimering af hus i ${city.name}`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-stone-950/65 backdrop-blur-[1px]"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/95 backdrop-blur-md p-8 rounded-3xl border border-white/60 shadow-xl space-y-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Energioptimering af hus i {city.name}
              </h2>
              <div className="space-y-3 text-xs sm:text-sm text-stone-600 leading-relaxed">
                <p>
                  Energioptimering af dit hus kan omfatte en række forskellige forbedringer, der både øger boligens komfort, reducerer energiforbruget og kan forbedre energimærket. En af de mest effektive løsninger er at efterisolere vægge, loft og gulv, da det kan mindske varmetabet betydeligt og sænke varmeudgifterne. Det kan også være en fordel at udskifte ældre vinduer og døre med moderne, energieffektive løsninger, som holder bedre på varmen og reducerer træk.
                </p>
                <p>
                  Derudover kan et nyt og mere effektivt varmeanlæg, eksempelvis en varmepumpe eller fjernvarme, bidrage til et lavere energiforbrug og en mere økonomisk opvarmning. Endelig kan installation af mekanisk ventilation med varmegenvinding forbedre indeklimaet, samtidig med at varmen i boligen udnyttes mere effektivt.
                </p>
              </div>

              <div className="p-4 rounded-2xl bg-stone-50/90 border border-stone-200 text-xs text-stone-600 space-y-2 mt-4">
                <div className="font-semibold text-stone-900">
                  Relaterede ydelser i {city.name}:
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to={`/trykproevning-i-${city.slug}`} className="text-emerald-700 hover:underline font-medium">
                    • Trykprøvning i {city.name}
                  </Link>
                  <Link to={`/energikonsulent-i-${city.slug}`} className="text-emerald-700 hover:underline font-medium">
                    • Energikonsulent i {city.name}
                  </Link>
                  <Link to="/termografi" className="text-emerald-700 hover:underline font-medium">
                    • Termografisk undersøgelse
                  </Link>
                  <Link to="/energiramme" className="text-emerald-700 hover:underline font-medium">
                    • Energirammeberegninger
                  </Link>
                  {["aarhus", "horsens", "odense"].includes(city.slug) ? (
                    <Link to={`/fugtmaaling-i-${city.slug}`} className="text-emerald-700 hover:underline font-medium">
                      • Fugtmåling i {city.name}
                    </Link>
                  ) : (
                    <Link to="/fugtmaaling" className="text-emerald-700 hover:underline font-medium">
                      • Fugtmåling med Tramex
                    </Link>
                  )}
                  {["aarhus", "horsens", "vejle", "odense"].includes(city.slug) ? (
                    <Link to={`/lca-beregning-i-${city.slug}`} className="text-emerald-700 hover:underline font-medium">
                      • LCA-beregning i {city.name}
                    </Link>
                  ) : (
                    <Link to="/lca-beregning" className="text-emerald-700 hover:underline font-medium">
                      • LCA-beregning efter BR18
                    </Link>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-md p-8 rounded-3xl border border-white/60 shadow-xl space-y-4">
              <h2 className="text-2xl font-bold text-stone-900 tracking-tight">
                Ny energimærkning – hvornår er det nødvendigt?
              </h2>
              <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                Ny energimærkning kan være nødvendig ved flere lejligheder:
              </p>
              <div className="space-y-3 text-xs sm:text-sm text-stone-700">
                <div className="p-3 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="font-bold text-stone-900 block mb-0.5">Salg eller udlejning :</span>
                  <span>Hvis du planlægger at sælge eller udleje din ejendom, skal du have en gyldig mærkning.</span>
                </div>
                <div className="p-3 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="font-bold text-stone-900 block mb-0.5">Renovering :</span>
                  <span>Efter større renoveringer kan det være en god idé at få en ny energimærkning, for at afspejle forbedringerne i ejendommens energieffektivitet.</span>
                </div>
                <div className="p-3 rounded-xl bg-stone-50 border border-stone-200/80">
                  <span className="font-bold text-stone-900 block mb-0.5">Regelmæssig opdatering :</span>
                  <span>mærkningerne har en gyldighed på 10 år. Efter denne periode skal du få udarbejdet en ny energimærkning.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. LOKAL ENERGIMÆRKNING & HVORFOR VÆLGE OS */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Energimærkning i {city.name}
              </h2>
              <p className="text-stone-700 text-sm leading-relaxed">
                Har du brug for en energimærkning i {city.name}? Vi tilbyder professionel energimærkning og energioptimering af huse og bygninger. Som certificerede energikonsulenter hjælper vi boligejere, virksomheder og boligforeninger med at få udarbejdet et gyldigt energimærke i {city.name} og omegn samt et overblik over ejendommens energiforbrug og muligheder for energiforbedringer.
              </p>
              <p className="text-stone-700 text-sm leading-relaxed">
                Med et indgående kendskab til boliger og bygninger i {city.name} sikrer vi en grundig gennemgang af din ejendom og en energimærkning, der lever op til alle gældende krav. Uanset om du skal sælge din bolig, udleje en ejendom eller blot ønsker at undersøge mulighederne for at reducere energiforbruget, står vi klar til at hjælpe dig med professionel rådgivning og en effektiv proces.
              </p>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="rounded-3xl overflow-hidden shadow-lg border border-stone-200">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07613.jpg"
                  alt={`Gennemgang til energimærkning i ${city.name}`}
                  className="w-full h-80 object-cover object-center"
                />
              </div>

              <div className="p-6 rounded-3xl bg-stone-900 text-white space-y-3">
                <h3 className="font-bold text-base">Hvorfor vælge os?</h3>
                <p className="text-xs text-stone-300 leading-relaxed">
                  Ved at vælge en erfaren og certificeret energikonsulent sikrer du, at din mærkning udføres professionelt og grundigt. Vi har stor erfaring med at vurdere både huse og bygninger, og vi kan hjælpe dig med at identificere de bedste muligheder for energioptimering af dit hus.
                </p>
                <div className="pt-2 border-t border-stone-800 flex items-center justify-between text-xs">
                  <span className="text-stone-400">Direkte kontakt:</span>
                  <a href="tel:+4542499595" className="text-emerald-400 font-bold hover:underline">
                    42 49 95 95
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CMS GALLERI */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <GalleryViewer
            gallerySlug="energimaerkning"
            title={`Galleri: Energimærkning i ${city.name} og Jylland & Fyn`}
            subtitle="Billeder fra besigtigelser, varmeanlæg og energigennemgange"
            maxImages={8}
          />
        </div>
      </section>

      {/* 6. FAQ OM ENERGIMÆRKNING */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Ofte stillede spørgsmål om energimærkning
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Få svar på de mest almindelige spørgsmål vedrørende energimærkning af huse og ejendomme
            </p>
          </div>

          <div className="space-y-3">
            {faqList.map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-stone-200 bg-stone-50/60 overflow-hidden transition-all"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full text-left p-5 font-bold text-stone-900 flex items-center justify-between gap-4 hover:bg-stone-100/70 transition-colors"
                  >
                    <span className="text-sm sm:text-base">{item.q}</span>
                    <i
                      className={`fa-solid fa-chevron-down text-xs text-stone-500 transition-transform ${
                        isOpen ? "rotate-180 text-emerald-600" : ""
                      }`}
                      aria-hidden="true"
                    ></i>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 text-xs sm:text-sm text-stone-600 leading-relaxed border-t border-stone-200/50 pt-3">
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 7. ANDRE BYER FOR ENERGIMÆRKNING */}
      <section className="py-12 bg-stone-100 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h3 className="text-lg font-bold text-stone-900 mb-2">
            Energimærkning i andre byer i Jylland og Fyn:
          </h3>
          <p className="text-xs text-stone-600 mb-6">
            Klik på en by for at se detaljer for energimærkning i dit område
          </p>
          <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
            {otherCities.map((c) => (
              <Link
                key={c.slug}
                to={`/energimaerkning-i-${c.slug}`}
                className="px-3.5 py-1.5 rounded-xl bg-white border border-stone-300/80 text-xs font-semibold text-stone-700 hover:bg-emerald-50 hover:border-emerald-500 hover:text-emerald-800 transition-all shadow-2xs"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 8. REVIEWS */}
      <ReviewsSlider />

      {/* 9. KONTAKTFORMULAR */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Bestil energimærkning i {city.name}
            </h2>
            <p className="text-sm text-stone-600 mt-2 max-w-lg mx-auto">
              Send os en besked for en hurtig gennemgang eller et uforpligtende tilbud.
            </p>
          </div>

          <div className="bg-white p-6 sm:p-10 rounded-3xl border border-stone-200 shadow-sm">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
